import React, { useContext, useMemo, useState, useEffect } from 'react';
import './Products.css';
import { LocaleContext, AuthContext, WishlistContext, CartContext } from '../locale';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

// helper: read cat from hash (#/products?cat=beer)
const getCatFromHash = () => {
  try {
    const m = window.location.hash.match(/cat=([^&]+)/i);
    const cat = m ? decodeURIComponent(m[1]) : 'all';
    const allowed = new Set(['all','beer','wine','whisky','snacks']);
    return allowed.has(cat) ? cat : 'all';
  } catch {
    return 'all';
  }
}

// Fallback products for when backend is not available
const FALLBACK_PRODUCTS = [
  { id: 'becks', category: 'beer', name: 'Becks', marque: 'Becks', type: 'Pilsner', color: 'Blonde', country: 'Germany', gamme: 'Standard', container: 'Bottle', price: 3.59, volume: '33cl', abv: 5.0, added: 2 },
  { id: 'heineken', category: 'beer', name: 'Heineken', marque: 'Heineken', type: 'Lager', color: 'Blonde', country: 'Netherlands', gamme: 'Standard', container: 'Can', price: 3.45, volume: '33cl', abv: 5.0, added: 5 },
  { id: 'k1664', category: 'beer', name: '1664 Blanc', marque: 'Kronenbourg', type: 'Wheat Beer', color: 'Blonde', country: 'France', gamme: 'Premium', container: 'Bottle', price: 3.99, volume: '33cl', abv: 5.0, added: 6 },
  { id: 'guinness', category: 'beer', name: 'Guinness Original', marque: 'Guinness', type: 'Stout', color: 'Brune', country: 'Ireland', gamme: 'Premium', container: 'Bottle', price: 4.25, volume: '33cl', abv: 4.2, added: 3 },
  { id: 'merlot', category: 'wine', name: 'Merlot', marque: 'Domaine du Soleil', type: 'Rouge', color: 'Rouge', country: 'France', gamme: 'Standard', container: 'Bottle', price: 12.95, volume: '75cl', abv: 13.0, added: 8 },
  { id: 'chardonnay', category: 'wine', name: 'Chardonnay', marque: 'Maison Bellevue', type: 'Blanc', color: 'Blanc', country: 'France', gamme: 'Standard', container: 'Bottle', price: 11.50, volume: '75cl', abv: 12.5, added: 4 },
  { id: 'pinot', category: 'wine', name: 'Pinot Noir', marque: 'Cuvée des Collines', type: 'Rouge', color: 'Rouge', country: 'France', gamme: 'Premium', container: 'Bottle', price: 14.90, volume: '75cl', abv: 13.5, added: 7 },
  { id: 'sauvignon', category: 'wine', name: 'Sauvignon Blanc', marque: 'Les Vignes Fraîches', type: 'Blanc', color: 'Blanc', country: 'France', gamme: 'Standard', container: 'Bottle', price: 10.95, volume: '75cl', abv: 12.0, added: 1 },
  // Added whisky example
  { id: 'highland12', category: 'whisky', name: 'Highland Reserve 12', marque: 'Highland Reserve', type: 'Scotch', color: 'Amber', country: 'Scotland', gamme: 'Premium', container: 'Bottle', price: 39.90, volume: '70cl', abv: 40.0, added: 9 },
  // Added snacks example (amuse‑gueules)
  { id: 'cheddar-bites', category: 'snacks', name: 'Cheddar Bites', marque: 'Gourmet Crave', type: 'Cheese Snack', color: '—', country: 'France', gamme: 'Standard', container: 'Pack', price: 4.50, volume: '100g', abv: 0.0, added: 10 }
];

function ProductCard({ p, t, locale, onAdd, onWishlist, wishlisted, onCart, inCart }) {
  const nf = new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' });
  const emoji =
    p.category === 'beer' ? '🍺' :
    p.category === 'wine' ? '🍷' :
    p.category === 'whisky' ? '🥃' : '🧀';

  return (
    <div
      className="prod-card"
      onClick={() => { window.location.hash = `#/products/${p.id}`; }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.location.hash = `#/products/${p.id}`; }}
    >
      <div className={`prod-media ${p.category}`}>
        {p.image_url ? (
          <img 
            src={p.image_url} 
            alt={p.name}
            className="product-image"
            loading="lazy"
            onError={(e) => {
              // Fallback to emoji if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
            onLoad={(e) => {
              // Ensure image is visible when loaded
              e.target.style.opacity = '1';
            }}
          />
        ) : null}
        <div className="emoji" aria-hidden style={{display: p.image_url ? 'none' : 'block'}}>
          {emoji}
        </div>

        {/* Wishlist button */}
        <button
          className={`card-action-btn wishlist-btn${wishlisted ? ' active' : ''}`}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          onClick={(e) => { e.stopPropagation(); onWishlist(); }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={wishlisted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </button>

        {/* Cart button */}
        <button
          className={`card-action-btn cart-btn${inCart ? ' active' : ''}`}
          aria-label={inCart ? 'Remove from cart' : 'Add to cart'}
          onClick={(e) => { e.stopPropagation(); onCart(); }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {inCart ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </>
            ) : (
              <>
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </>
            )}
          </svg>
        </button>
      </div>
      <div className="prod-body">
        <div className="prod-name">{p.name}</div>
        <div className="prod-meta">{p.type} · {p.marque}</div>
        <div className="prod-spec">{p.abv}% vol · {p.volume}</div>
      </div>
      <div className="prod-foot">
        <div className="prod-price">{nf.format(p.price)}</div>
        <button className="btn add" onClick={(e) => { e.stopPropagation(); onAdd(p); }}>{t('common.addToCart')}</button>
      </div>
    </div>
  );
}

export default function Products() {
  const { t, locale } = useContext(LocaleContext);
  const { user, openAuth } = useContext(AuthContext);
  const { wishlist, addToWishlist, removeFromWishlist, isInWishlist } = useContext(WishlistContext);
  const { cart, addToCart, removeFromCart, isInCart } = useContext(CartContext);
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(getCatFromHash());
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({
    search: true, brewery: false, color: false, style: false, country: false, gamme: false, abv: false, container: false, volume: false
  });
  const [selected, setSelected] = useState({
    brewery: new Set(), color: new Set(), style: new Set(), country: new Set(), gamme: new Set(), container: new Set(), volume: new Set(), abv: new Set()
  });

  // Load products from backend
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch(`${API_BASE}/products`);
        if (response.ok) {
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            const processedProducts = data.products.map((p, index) => ({
              ...p,
              price: parseFloat(p.price),
              abv: parseFloat(p.abv),
              added: index + 1
            }));
            setProducts(processedProducts);
          } else {
            setProducts(FALLBACK_PRODUCTS);
          }
        } else {
          setProducts(FALLBACK_PRODUCTS);
        }
      } catch (error) {
        console.warn('Failed to load products from backend:', error.message);
        setProducts(FALLBACK_PRODUCTS);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const baseList = useMemo(() => products.filter(p => (filter === 'all' ? true : p.category === filter)), [products, filter]);

  const options = useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr)).sort();
    return {
      brewery: uniq(baseList.map(p => p.marque)),
      color: uniq(baseList.map(p => p.color)),
      style: uniq(baseList.map(p => p.type)),
      country: uniq(baseList.map(p => p.country)),
      gamme: uniq(baseList.map(p => p.gamme)),
      container: uniq(baseList.map(p => p.container)),
      volume: uniq(baseList.map(p => p.volume))
    };
  }, [baseList]);

  const ABV_RANGES = [
    { id: 'lt5', test: (x) => x < 5 },
    { id: 'mid', test: (x) => x >= 5 && x <= 10 },
    { id: 'gt10', test: (x) => x > 10 }
  ];

  const toggleSection = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));
  const toggleSel = (group, value) => {
    setSelected(prev => {
      const next = new Set(prev[group]);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...prev, [group]: next };
    });
  };

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const passes = (p) => {
      if (q && !(p.name.toLowerCase().includes(q) || p.marque.toLowerCase().includes(q))) return false;
      const matchSet = (group, val) => selected[group].size === 0 || selected[group].has(val);
      if (!matchSet('brewery', p.marque)) return false;
      if (!matchSet('color', p.color)) return false;
      if (!matchSet('style', p.type)) return false;
      if (!matchSet('country', p.country)) return false;
      if (!matchSet('gamme', p.gamme)) return false;
      if (!matchSet('container', p.container)) return false;
      if (!matchSet('volume', p.volume)) return false;
      if (selected.abv.size > 0) {
        const ok = ABV_RANGES.some(r => selected.abv.has(r.id) && r.test(p.abv));
        if (!ok) return false;
      }
      return true;
    };

    const filtered = baseList.filter(passes);
    return [...filtered].sort((a, b) => {
      if (sort === 'price-asc') return a.price - b.price;
      if (sort === 'price-desc') return b.price - a.price;
      return (b.added ?? 0) - (a.added ?? 0);
    });
  }, [baseList, search, selected, sort]);

  const handleAdd = (product, qty = 1) => {
    if (!user) {
      openAuth('login');
      return;
    }
    // Pass quantity both as a prop on the product and as an explicit param (compatible with most carts)
    addToCart({ ...product, quantity: qty }, qty);
  };

  const handleCartToggle = (product) => {
    if (!user) {
      openAuth('login');
      return;
    }
    if (isInCart(product.id)) {
      removeFromCart(product.id);
    } else {
      addToCart({ ...product, quantity: 1 }, 1);
    }
  };

  const handleWishlistToggle = (product) => {
    if (!user) {
      openAuth('login');
      return;
    }
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  // keep filter in sync when hash changes (e.g., from landing deep links)
  useEffect(() => {
    const onHash = () => setFilter(getCatFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (loading) {
    return (
      <section id="products" className="products-page">
        <div className="container">
          <div className="page-head">
            <h2 className="h2">{t('productsPage.title')}</h2>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 0',
            color: 'var(--muted)'
          }}>
            <div style={{
              width: 40,
              height: 40,
              border: '3px solid rgba(212,154,106,0.3)',
              borderTop: '3px solid var(--accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: 16
            }} />
            Chargement des produits...
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="products-page">
      <div className="container">
        <div className="page-head">
          <h2 className="h2">{t('productsPage.title')}</h2>
        </div>

        <div className="products-layout">
          <aside className="filters-panel">
            <div className="filters-title">{t('productsPage.filters.title')}</div>
            <div className={`acc ${open.search ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('search')}>
                {t('productsPage.filters.search')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                <input
                  className="search-input"
                  placeholder={t('productsPage.filters.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className={`acc ${open.brewery ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('brewery')}>
                {t('productsPage.filters.brewery')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.brewery.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.brewery.has(v)} onChange={() => toggleSel('brewery', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.color ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('color')}>
                {t('productsPage.filters.color')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.color.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.color.has(v)} onChange={() => toggleSel('color', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.style ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('style')}>
                {t('productsPage.filters.style')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.style.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.style.has(v)} onChange={() => toggleSel('style', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.country ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('country')}>
                {t('productsPage.filters.country')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.country.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.country.has(v)} onChange={() => toggleSel('country', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.gamme ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('gamme')}>
                {t('productsPage.filters.gamme')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.gamme.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.gamme.has(v)} onChange={() => toggleSel('gamme', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.abv ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('abv')}>
                {t('productsPage.filters.abv')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {ABV_RANGES.map(r => (
                  <label key={r.id} className="chk">
                    <input
                      type="checkbox"
                      checked={selected.abv.has(r.id)}
                      onChange={() => toggleSel('abv', r.id)}
                    />
                    <span>{t(`productsPage.filters.abvRanges.${r.id}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.container ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('container')}>
                {t('productsPage.filters.container')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.container.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.container.has(v)} onChange={() => toggleSel('container', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`acc ${open.volume ? 'open' : ''}`}>
              <button className="acc-head" onClick={() => toggleSection('volume')}>
                {t('productsPage.filters.volume')} <span className="plus" aria-hidden />
              </button>
              <div className="acc-body">
                {options.volume.map(v => (
                  <label key={v} className="chk">
                    <input type="checkbox" checked={selected.volume.has(v)} onChange={() => toggleSel('volume', v)} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>
          </aside>

          <div className="results">
            <div className="controls">
              <div className="filters">
                <button className={`chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                  {t('productsPage.all')}
                </button>
                <button className={`chip ${filter === 'beer' ? 'active' : ''}`} onClick={() => setFilter('beer')}>
                  {t('productsPage.beer')}
                </button>
                <button className={`chip ${filter === 'wine' ? 'active' : ''}`} onClick={() => setFilter('wine')}>
                  {t('productsPage.wine')}
                </button>
                <button className={`chip ${filter === 'whisky' ? 'active' : ''}`} onClick={() => setFilter('whisky')}>
                  {t('productsPage.whisky')}
                </button>
                <button className={`chip ${filter === 'snacks' ? 'active' : ''}`} onClick={() => setFilter('snacks')}>
                  {t('productsPage.snacks')}
                </button>
              </div>
              <div className="sort">
                <span className="sort-label">{t('productsPage.sortBy')}:</span>
                <div className="select">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label={t('productsPage.sortBy')}
                  >
                    <option value="newest">{t('productsPage.sort.newest')}</option>
                    <option value="price-asc">{t('productsPage.sort.priceAsc')}</option>
                    <option value="price-desc">{t('productsPage.sort.priceDesc')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid">
              {list.map(p => (
                <ProductCard
                  key={p.id}
                  p={p}
                  t={t}
                  locale={locale}
                  onAdd={(prod) => handleAdd(prod, 1)}
                  onWishlist={() => handleWishlistToggle(p)}
                  wishlisted={isInWishlist(p.id)}
                  onCart={() => handleCartToggle(p)}
                  inCart={isInCart(p.id)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
